/**
 * Client-side mirror of server/modules/shared/roros-enums.ts WORK_ITEM_TRANSITIONS.
 *
 * Used by the Kanban Board to validate drag targets client-side before
 * calling the transition API. Keep in sync with the server enum if the
 * state machine changes.
 */

export const WORK_ITEM_TRANSITIONS: Readonly<Record<string, readonly string[]>> = {
  backlog:               ['ready'],
  ready:                 ['in_progress', 'backlog'],
  in_progress:           ['done', 'blocked', 'escalated'],
  done:                  ['quality_confirmation', 'escalated'],
  quality_confirmation:  ['released', 'in_progress'],
  released:              [],
  blocked:               ['in_progress', 'escalated'],
  escalated:             ['in_progress', 'blocked'],
} as const;

/** Statuses that belong to the Exceptions column */
export const EXCEPTIONS_STATUSES = ['blocked', 'escalated'] as const;
export type ExceptionStatus = (typeof EXCEPTIONS_STATUSES)[number];

/**
 * Map a canonical board column id to its underlying status value(s).
 * 'exceptions' maps to both blocked + escalated.
 */
export const COLUMN_STATUSES: Record<string, string[]> = {
  backlog:               ['backlog'],
  ready:                 ['ready'],
  in_progress:           ['in_progress'],
  done:                  ['done'],
  quality_confirmation:  ['quality_confirmation'],
  released:              ['released'],
  exceptions:            ['blocked', 'escalated'],
};

/**
 * Returns allowed drop-target column ids for a given source status.
 * 'exceptions' is a valid target if any of blocked/escalated is in transitions.
 */
export function getAllowedTargetColumns(sourceStatus: string): string[] {
  const directTargets = WORK_ITEM_TRANSITIONS[sourceStatus] ?? [];
  const targets: string[] = [];

  for (const [colId, statuses] of Object.entries(COLUMN_STATUSES)) {
    if (colId === 'exceptions') {
      // Exceptions column is valid if any of blocked/escalated is in direct targets
      if (statuses.some((s) => directTargets.includes(s))) {
        targets.push('exceptions');
      }
    } else {
      if (statuses.some((s) => directTargets.includes(s))) {
        targets.push(colId);
      }
    }
  }
  return targets;
}
